/**
 * Cart drawer related products (Shopify recommendations API).
 * Imperative init — resilient to Fabric morph/hydration.
 */
(() => {
  const CART_UPDATE_EVENT = 'cart:update';
  /** @type {WeakMap<HTMLElement, AbortController>} */
  const controllers = new WeakMap();
  /** @type {WeakSet<HTMLElement>} */
  const inFlight = new WeakSet();
  let scanTimer = 0;

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(amount);
    } catch {
      return `R$ ${amount.toFixed(2).replace('.', ',')}`;
    }
  }

  function parseExcludeIds(value) {
    return new Set(
      String(value || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  function pickVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const available = variants.find((v) => v && v.available !== false);
    return available || variants[0] || null;
  }

  function imageUrl(product) {
    if (!product) return '';
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.src) return product.featured_image.src;
    if (product.images?.[0]) {
      return typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src || '';
    }
    if (product.media?.[0]?.preview_image?.src) return product.media[0].preview_image.src;
    return '';
  }

  function isProductAvailable(product) {
    if (!product) return false;
    if (product.available === false) return false;
    if (product.available === true) return true;
    const variant = pickVariant(product);
    return Boolean(variant && variant.available !== false);
  }

  function filterProducts(products, exclude, limit) {
    return (products || [])
      .filter((p) => p && !exclude.has(String(p.id)) && isProductAvailable(p))
      .slice(0, limit);
  }

  function collectSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  async function fetchRecommendations(productId, limit, intent, signal) {
    const url = `/recommendations/products.json?product_id=${encodeURIComponent(
      productId
    )}&limit=${limit + 8}&intent=${encodeURIComponent(intent)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' }, signal });
    if (!res.ok) return [];
    const data = await res.json();
    return data.products || [];
  }

  async function fetchCatalogFallback(limit, signal) {
    const res = await fetch(`/collections/all/products.json?limit=${limit + 12}`, {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.products || [];
  }

  function render(el, list, products) {
    list.innerHTML = '';
    const addLabel = el.dataset.addLabel || 'Adicionar';
    const chooseLabel = el.dataset.chooseLabel || 'Escolher';
    const frag = document.createDocumentFragment();

    for (const product of products) {
      const variant = pickVariant(product);
      const hasOnlyDefault =
        product.has_only_default_variant === true ||
        (Array.isArray(product.variants) && product.variants.length <= 1);
      const canQuickAdd = Boolean(variant?.id && variant.available !== false && hasOnlyDefault);

      const li = document.createElement('li');
      li.className = 'cart-drawer-related__item';

      const media = document.createElement('a');
      media.className = 'cart-drawer-related__media';
      media.href = product.url || `/products/${product.handle}`;
      media.setAttribute('tabindex', '-1');
      media.setAttribute('aria-hidden', 'true');
      const src = imageUrl(product);
      if (src) {
        const img = document.createElement('img');
        img.src = src.includes('?') ? `${src}&width=112` : `${src}?width=112`;
        img.alt = '';
        img.width = 56;
        img.height = 56;
        img.loading = 'lazy';
        media.appendChild(img);
      }

      const info = document.createElement('div');
      info.className = 'cart-drawer-related__info';

      const name = document.createElement('a');
      name.className = 'cart-drawer-related__name cart-primary-typography';
      name.href = product.url || `/products/${product.handle}`;
      name.textContent = product.title || '';

      const price = document.createElement('div');
      price.className = 'cart-drawer-related__price cart-secondary-typography';
      const priceCents = Number(variant?.price ?? product.price) || 0;
      const compare = Number(variant?.compare_at_price ?? product.compare_at_price) || 0;
      if (compare > priceCents && compare > 0) {
        const s = document.createElement('s');
        s.textContent = formatMoney(compare);
        price.appendChild(s);
      }
      price.appendChild(document.createTextNode(formatMoney(priceCents)));

      info.appendChild(name);
      info.appendChild(price);

      let action;
      if (canQuickAdd) {
        action = document.createElement('button');
        action.type = 'button';
        action.className = 'button button-secondary cart-drawer-related__action';
        action.textContent = addLabel;
        action.addEventListener('click', () => addToCart(variant.id, action, el));
      } else {
        action = document.createElement('a');
        action.className = 'button button-secondary cart-drawer-related__action';
        action.href = product.url || `/products/${product.handle}`;
        action.textContent = chooseLabel;
      }

      li.appendChild(media);
      li.appendChild(info);
      li.appendChild(action);
      frag.appendChild(li);
    }

    list.appendChild(frag);
  }

  async function addToCart(variantId, button, el) {
    if (!variantId) return;
    const addedLabel = el.dataset.addedLabel || 'Adicionado';
    const errorLabel = el.dataset.errorLabel || 'Erro ao adicionar';
    const addLabel = el.dataset.addLabel || 'Adicionar';

    button.dataset.loading = '';
    button.disabled = true;

    try {
      const sectionIds = collectSectionIds();
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || 'add');
      }

      button.textContent = addedLabel;
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'cart-drawer-related',
            data: {
              source: 'cart-drawer-related',
              itemCount: data.item_count,
              sections: data.sections,
            },
          },
        })
      );
    } catch {
      button.textContent = errorLabel;
      setTimeout(() => {
        button.textContent = addLabel;
        button.disabled = false;
        delete button.dataset.loading;
      }, 1800);
      return;
    }

    delete button.dataset.loading;
  }

  async function loadRelated(el) {
    if (!(el instanceof HTMLElement)) return;

    const list = el.querySelector('[data-related-list]');
    const productId = el.dataset.productId;
    const limit = Math.max(1, Math.min(8, Number(el.dataset.limit) || 3));
    const intent = el.dataset.intent || 'related';
    const key = `${productId}|${el.dataset.excludeIds || ''}|${limit}|${intent}`;

    if (!productId || !list) {
      el.hidden = true;
      return;
    }

    if (el.dataset.relatedKey === key && !el.hidden && list.children.length) return;
    if (inFlight.has(el)) return;

    inFlight.add(el);
    const controller = new AbortController();
    controllers.get(el)?.abort();
    controllers.set(el, controller);

    try {
      const exclude = parseExcludeIds(el.dataset.excludeIds);
      let products = filterProducts(
        await fetchRecommendations(productId, limit, intent, controller.signal),
        exclude,
        limit
      );

      if (!products.length) {
        products = filterProducts(await fetchCatalogFallback(limit, controller.signal), exclude, limit);
      }

      const listNow = el.querySelector('[data-related-list]');
      if (!listNow || !document.contains(el)) return;

      if (!products.length) {
        listNow.innerHTML = '';
        el.hidden = true;
        delete el.dataset.relatedKey;
        return;
      }

      render(el, listNow, products);
      el.dataset.relatedKey = key;
      el.hidden = false;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      el.hidden = true;
    } finally {
      inFlight.delete(el);
    }
  }

  function scan() {
    document.querySelectorAll('cart-drawer-related').forEach((el) => {
      loadRelated(el);
    });
  }

  function scheduleScan(delay = 100) {
    window.clearTimeout(scanTimer);
    scanTimer = window.setTimeout(scan, delay);
  }

  function resetAndScan() {
    document.querySelectorAll('cart-drawer-related').forEach((el) => {
      delete el.dataset.relatedKey;
    });
    scheduleScan(150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleScan(0), { once: true });
  } else {
    scheduleScan(0);
  }

  // Safety net after theme hydration / late cart morph.
  scheduleScan(400);
  scheduleScan(1200);

  document.addEventListener(CART_UPDATE_EVENT, resetAndScan);

  const mo = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches?.('cart-drawer-related') || node.querySelector?.('cart-drawer-related')) {
          scheduleScan(150);
          return;
        }
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
